import React, { useState } from 'react';
import { IdealCustomerProfile } from '../../../types';
import { Modal, Button, Input, SearchableMultiSelect, ToggleButtonGroup, MultiToggleButtonGroup } from '../../ui';
import { ALL_INDUSTRIES, ALL_REGIONS, INDUSTRY_QUICK_SELECTS, REGION_QUICK_SELECTS, COMPANY_SIZES, CUSTOMER_TYPES, SELLING_MOTIONS } from '../../../constants/icpOptions';

interface EditICPModalProps {
  icp: IdealCustomerProfile;
  isOpen: boolean;
  onClose: () => void;
  onSave: (icp: IdealCustomerProfile, formData: EditICPFormData) => Promise<void>;
  isSaving: boolean;
}

export interface EditICPFormData {
  name: string;
  solution_products: string;
  customer_type: string;
  selling_motion: string;
  company_sizes: string[];
  target_verticals: string[];
  titles: string;
  target_region: string[];
}

export const EditICPModal: React.FC<EditICPModalProps> = ({
  icp,
  isOpen,
  onClose,
  onSave,
  isSaving,
}) => {
  const [form, setForm] = useState<EditICPFormData>({
    name: icp.name || '',
    solution_products: icp.solution_products || '',
    customer_type: icp.customer_type || '',
    selling_motion: icp.selling_motion || '',
    company_sizes: icp.company_sizes || [],
    target_verticals: icp.target_verticals || [],
    titles: icp.titles || '',
    target_region: Array.isArray(icp.target_region) ? icp.target_region : [],
  });

  const handleSave = async () => {
    if (!form.name.trim()) return;
    await onSave(icp, form);
  };

  const updateForm = <K extends keyof EditICPFormData>(key: K, value: EditICPFormData[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      showCloseButton={false}
    >
      <div className="-mx-6 -mt-4">
        <div className="px-5 pt-5 pb-4">
          <div className="flex items-start justify-between mb-1">
            <span className="text-2xs font-medium text-neutral-400 uppercase tracking-wider">Edit Profile</span>
          </div>
          <input
            type="text"
            value={form.name}
            onChange={(e) => updateForm('name', e.target.value)}
            placeholder="Profile name..."
            className="w-full text-base font-semibold text-neutral-900 bg-transparent border-none outline-none placeholder-neutral-300 p-0"
            autoFocus
          />
        </div>

        <div className="px-5 pb-5 space-y-4">
          <div>
            <span className="text-2xs font-medium text-neutral-400 uppercase tracking-wide">What do you sell?</span>
            <textarea
              value={form.solution_products}
              onChange={(e) => updateForm('solution_products', e.target.value)}
              placeholder="Describe your product or service..."
              rows={2}
              className="w-full mt-1.5 px-3 py-2 text-13 text-neutral-700 bg-white border border-neutral-200 rounded-lg focus:border-neutral-300 focus:ring-1 focus:ring-neutral-100 outline-none resize-none"
            />
          </div>

          <div className="flex gap-4">
            <ToggleButtonGroup
              label="Customer"
              options={CUSTOMER_TYPES}
              value={form.customer_type}
              onChange={(value) => updateForm('customer_type', value)}
            />
            <ToggleButtonGroup
              label="Motion"
              options={SELLING_MOTIONS}
              value={form.selling_motion}
              onChange={(value) => updateForm('selling_motion', value)}
            />
          </div>

          <Input
            label="Target Titles"
            value={form.titles}
            onChange={(e) => updateForm('titles', e.target.value)}
            placeholder="VP Sales, CRO, Head of Revenue..."
            fullWidth
          />

          <MultiToggleButtonGroup
            label="Company Size"
            options={COMPANY_SIZES}
            values={form.company_sizes}
            onChange={(values) => updateForm('company_sizes', values)}
          />

          <SearchableMultiSelect
            label="Industries"
            placeholder="Search industries..."
            options={ALL_INDUSTRIES}
            selectedValues={form.target_verticals}
            onChange={(values) => updateForm('target_verticals', values)}
            quickSelects={INDUSTRY_QUICK_SELECTS}
          />

          <SearchableMultiSelect
            label="Regions"
            placeholder="Search regions..."
            options={ALL_REGIONS}
            selectedValues={form.target_region}
            onChange={(values) => updateForm('target_region', values)}
            quickSelects={REGION_QUICK_SELECTS}
          />
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-neutral-100">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={!form.name.trim()}
            loading={isSaving}
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};
